/**
 * Landing page — marketing + professional DSS product story.
 * Hero, motion, plans — claims stay honest to the product.
 */
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Button,
  Typography,
  AppBar,
  Toolbar,
  Stack,
  Paper,
  Grid,
  Card,
  CardContent,
  Chip,
  Fade,
  Grow,
  Divider,
  Link,
  useTheme,
  useMediaQuery,
  keyframes,
} from '@mui/material';
import {
  WaterDrop,
  Science,
  Assessment,
  Verified,
  ArrowForward,
  PlayArrow,
  CheckCircleOutline,
} from '@mui/icons-material';
import { RootState } from '../../store';
import Logo from '../common/Logo';
import { useImagePreloader, BACKGROUND_IMAGES } from '../common/ImagePreloader';

const year = new Date().getFullYear();

const floatY = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-8px); }
`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(28px); }
  to { opacity: 1; transform: translateY(0); }
`;

const shimmer = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

/** Scroll-reveal variants — CSS keyframes (reliable; not opacity stuck at 0) */
type RevealVariant = 'up' | 'flip' | 'left' | 'right' | 'scale' | 'fade';

const animUp = keyframes`
  from { opacity: 0; transform: translate3d(0, 48px, 0); }
  to   { opacity: 1; transform: translate3d(0, 0, 0); }
`;
const animFlip = keyframes`
  from { opacity: 0; transform: perspective(1200px) rotateX(18deg) translate3d(0, 36px, 0); }
  to   { opacity: 1; transform: perspective(1200px) rotateX(0deg) translate3d(0, 0, 0); }
`;
const animLeft = keyframes`
  from { opacity: 0; transform: translate3d(-56px, 0, 0); }
  to   { opacity: 1; transform: translate3d(0, 0, 0); }
`;
const animRight = keyframes`
  from { opacity: 0; transform: translate3d(56px, 0, 0); }
  to   { opacity: 1; transform: translate3d(0, 0, 0); }
`;
const animScale = keyframes`
  from { opacity: 0; transform: scale(0.9) translate3d(0, 20px, 0); }
  to   { opacity: 1; transform: scale(1) translate3d(0, 0, 0); }
`;
const animFade = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const REVEAL_KEYFRAMES: Record<RevealVariant, typeof animUp> = {
  up: animUp,
  flip: animFlip,
  left: animLeft,
  right: animRight,
  scale: animScale,
  fade: animFade,
};

const PIPELINE = [
  { n: '1', name: 'Water', desc: 'Source ions, pH, EC as background' },
  { n: '2', name: 'Crop', desc: 'Target nutrient setpoints' },
  { n: '3', name: 'Salts', desc: 'Fertilizers & saved combinations' },
  { n: '4', name: 'Recipe', desc: 'Solve · tanks · verify' },
  { n: '5', name: 'Results', desc: 'Balance, ratios, PDF export' },
];

const FEATURES = [
  {
    icon: <WaterDrop sx={{ fontSize: 36 }} />,
    title: 'Water-aware formulation',
    body: 'Background ions from your source water are subtracted in the solve — not ignored.',
  },
  {
    icon: <Science sx={{ fontSize: 36 }} />,
    title: 'HydroBuddy-class matrix',
    body: 'Least-squares salt doses from crop targets, purity, volume, and degree of freedom.',
  },
  {
    icon: <Assessment sx={{ fontSize: 36 }} />,
    title: 'A/B stock & dilution',
    body: 'Concentration factor, tank logistics, and clear mL-per-liter working instructions.',
  },
  {
    icon: <Verified sx={{ fontSize: 36 }} />,
    title: 'Verification & report',
    body: 'Math gates, nutrient balance, ratios, and a tabbed professional Results report.',
  },
];

const PLANS = [
  {
    name: 'Free',
    price: '0',
    currency: '$',
    period: 'per month',
    blurb: 'Explore the DSS',
    audience: 'Students & evaluation',
    points: [
      'Full Water → Results pipeline',
      'Limited recipe calculations',
      'Documentation & support hub',
      'Community access',
    ],
    cta: 'Get started free',
    action: 'free' as const,
    highlight: false,
  },
  {
    name: 'Professional',
    price: '29',
    currency: '$',
    period: 'per month',
    blurb: 'Production operations',
    audience: 'Growers & consultants',
    points: [
      'Unlimited professional recipes',
      'PDF report export',
      'Salt combinations & verify gates',
      'A/B stock logistics',
      'Priority email support',
    ],
    cta: 'Start free trial',
    action: 'pro' as const,
    highlight: true,
  },
  {
    name: 'Enterprise',
    price: 'Custom',
    currency: '',
    period: 'tailored licensing',
    blurb: 'Organizations at scale',
    audience: 'Multi-site & teams',
    points: [
      'Everything in Professional',
      'Team seats & roles',
      'Custom salt libraries',
      'Advanced reporting options',
      'Dedicated onboarding',
    ],
    cta: 'Talk to sales',
    action: 'enterprise' as const,
    highlight: false,
  },
];

/**
 * Scroll reveal — only animates when the element scrolls into the lower 70% of the viewport
 * (so sections below the hero are not “already done” on first paint).
 */
const Reveal: React.FC<{
  children: React.ReactNode;
  delayMs?: number;
  variant?: RevealVariant;
  durationMs?: number;
  block?: boolean;
}> = ({
  children,
  delayMs = 0,
  variant = 'up',
  durationMs = 1000,
  block = true,
}) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const [phase, setPhase] = useState<'wait' | 'play' | 'done'>('wait');

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setPhase('done');
      return;
    }

    // Never leave content invisible forever
    const safety = window.setTimeout(() => setPhase((p) => (p === 'wait' ? 'done' : p)), 8000);

    const tryReveal = () => {
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight || 800;
      // Enter when the top of the block is above ~88% of the viewport (user has scrolled to it)
      const entered =
        rect.top < vh * 0.88 && rect.bottom > vh * 0.08;
      if (entered) {
        setPhase('play');
        window.clearTimeout(safety);
        return true;
      }
      return false;
    };

    if (tryReveal()) {
      // Already in view on load — still play animation once
    }

    const onScroll = () => {
      if (tryReveal()) {
        window.removeEventListener('scroll', onScroll, true);
        window.removeEventListener('resize', onScroll);
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true, capture: true });
    window.addEventListener('resize', onScroll, { passive: true });

    // Also use IO as a second signal
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.08) {
          setPhase('play');
          window.clearTimeout(safety);
          io.disconnect();
          window.removeEventListener('scroll', onScroll, true);
          window.removeEventListener('resize', onScroll);
        }
      },
      { threshold: [0.08, 0.15, 0.25], rootMargin: '0px 0px -12% 0px' }
    );
    io.observe(el);

    return () => {
      window.clearTimeout(safety);
      io.disconnect();
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  const playing = phase === 'play';

  return (
    <Box
      ref={ref}
      sx={{
        width: block ? '100%' : undefined,
        height: block ? '100%' : undefined,
        // wait = hidden; play = keyframes; done (safety) = visible static
        opacity: phase === 'wait' ? 0 : 1,
        animation: playing
          ? `${REVEAL_KEYFRAMES[variant]} ${durationMs}ms cubic-bezier(0.16, 1, 0.3, 1) ${delayMs}ms both`
          : 'none',
        transformOrigin: '50% 100%',
        '@media (prefers-reduced-motion: reduce)': {
          opacity: 1,
          animation: 'none !important',
        },
      }}
      onAnimationEnd={() => setPhase('done')}
    >
      {children}
    </Box>
  );
};

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isAuthenticated } = useSelector((s: RootState) => s.auth);
  const [heroIn, setHeroIn] = useState(false);

  useImagePreloader([BACKGROUND_IMAGES.hero], true);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    const t = requestAnimationFrame(() => setHeroIn(true));
    return () => cancelAnimationFrame(t);
  }, []);

  const goRegister = (plan?: string) => {
    navigate(plan ? `/register?plan=${plan}` : '/register');
  };

  const contactEnterprise = () => {
    const subject = encodeURIComponent('FlahaFAST Enterprise inquiry');
    const body = encodeURIComponent(
      'I would like information on FlahaFAST Enterprise.\n\nOrganization:\nScale:\nContact:\n'
    );
    window.location.href = `mailto:info@flaha.org?subject=${subject}&body=${body}`;
  };

  return (
    <Box
      component="main"
      id="main-content"
      tabIndex={-1}
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.default',
        outline: 'none',
      }}
    >
      {/* Nav */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          bgcolor: 'rgba(255,255,255,0.92)',
          backdropFilter: 'blur(12px)',
          borderBottom: 1,
          borderColor: 'divider',
          color: 'text.primary',
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 56, sm: 64 }, gap: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ flexGrow: 1 }}>
            <Logo type="flahafast" size="small" />
            <Typography
              variant="h6"
              fontWeight={700}
              color="primary.main"
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              FlahaFAST
            </Typography>
            <Chip
              label="DSS"
              size="small"
              color="primary"
              variant="outlined"
              sx={{ height: 22, display: { xs: 'none', md: 'inline-flex' } }}
            />
          </Stack>
          {!isMobile && (
            <Stack direction="row" spacing={0.5} sx={{ mr: 1 }}>
              <Button color="inherit" href="#features" sx={{ color: 'text.secondary' }}>
                Features
              </Button>
              <Button color="inherit" href="#workflow" sx={{ color: 'text.secondary' }}>
                Workflow
              </Button>
              <Button color="inherit" href="#pricing" sx={{ color: 'text.secondary' }}>
                Pricing
              </Button>
              <Button
                color="inherit"
                component={RouterLink}
                to="/support"
                sx={{ color: 'text.secondary' }}
              >
                Support
              </Button>
            </Stack>
          )}
          <Button size="small" variant="outlined" onClick={() => navigate('/login')}>
            Sign in
          </Button>
          <Button size="small" variant="contained" onClick={() => goRegister()}>
            Get started
          </Button>
        </Toolbar>
      </AppBar>

      {/* —— HERO —— */}
      <Box
        component="section"
        sx={{
          position: 'relative',
          minHeight: { xs: '88vh', md: '92vh' },
          display: 'flex',
          alignItems: 'center',
          pt: { xs: 10, md: 8 },
          pb: 6,
          overflow: 'hidden',
          background: `
            linear-gradient(125deg,
              rgba(5, 124, 105, 0.88) 0%,
              rgba(11, 125, 157, 0.82) 45%,
              rgba(21, 191, 213, 0.75) 100%),
            url('/assets/images/hero-background.jpg')
          `,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundAttachment: { xs: 'scroll', md: 'fixed' },
        }}
      >
        {/* soft animated overlay */}
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            inset: 0,
            background:
              'radial-gradient(ellipse 80% 60% at 70% 40%, rgba(255,255,255,0.12) 0%, transparent 55%)',
            animation: `${shimmer} 12s ease infinite`,
            backgroundSize: '200% 200%',
            pointerEvents: 'none',
          }}
        />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={4} alignItems="center">
            <Grid size={{ xs: 12, md: 7 }}>
              <Fade in={heroIn} timeout={900}>
                <Box sx={{ color: 'common.white' }}>
                  <Chip
                    label="Nutrient Decision Support System"
                    size="small"
                    sx={{
                      mb: 2,
                      bgcolor: 'rgba(255,255,255,0.15)',
                      color: 'white',
                      border: '1px solid rgba(255,255,255,0.35)',
                      fontWeight: 600,
                    }}
                  />
                  <Typography
                    variant="h2"
                    component="h1"
                    sx={{
                      fontWeight: 800,
                      fontSize: { xs: '2.15rem', sm: '2.75rem', md: '3.25rem' },
                      lineHeight: 1.15,
                      mb: 2,
                      textShadow: '0 2px 24px rgba(0,0,0,0.25)',
                      animation: heroIn ? `${fadeUp} 0.85s ease both` : undefined,
                    }}
                  >
                    Professional hydroponic
                    <br />
                    nutrient formulation
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 400,
                      opacity: 0.95,
                      mb: 3.5,
                      maxWidth: 520,
                      lineHeight: 1.65,
                      textShadow: '0 1px 8px rgba(0,0,0,0.2)',
                      animation: heroIn ? `${fadeUp} 1s ease 0.12s both` : undefined,
                    }}
                  >
                    Turn water quality, crop targets, and fertilizer salts into
                    defensible stock recipes — matrix solve, A/B logistics, verification,
                    and export-ready results.
                  </Typography>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1.5}
                    sx={{
                      animation: heroIn ? `${fadeUp} 1s ease 0.22s both` : undefined,
                    }}
                  >
                    <Button
                      variant="contained"
                      size="large"
                      endIcon={<ArrowForward />}
                      onClick={() => goRegister('pro')}
                      sx={{
                        px: 3.5,
                        py: 1.35,
                        fontWeight: 700,
                        bgcolor: 'common.white',
                        color: 'primary.dark',
                        '&:hover': {
                          bgcolor: 'grey.100',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 12px 32px rgba(0,0,0,0.2)',
                        },
                        transition: 'all 0.25s ease',
                      }}
                    >
                      Start free trial
                    </Button>
                    <Button
                      variant="outlined"
                      size="large"
                      startIcon={<PlayArrow />}
                      onClick={() => navigate('/support')}
                      sx={{
                        px: 3,
                        py: 1.35,
                        fontWeight: 600,
                        borderColor: 'rgba(255,255,255,0.7)',
                        color: 'common.white',
                        '&:hover': {
                          borderColor: 'common.white',
                          bgcolor: 'rgba(255,255,255,0.1)',
                        },
                      }}
                    >
                      See how it works
                    </Button>
                  </Stack>
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      mt: 2.5,
                      opacity: 0.85,
                      animation: heroIn ? `${fadeUp} 1s ease 0.35s both` : undefined,
                    }}
                  >
                    Built for commercial growers, consultants, and agronomy teams
                  </Typography>
                </Box>
              </Fade>
            </Grid>

            {!isMobile && (
              <Grid size={{ md: 5 }}>
                <Grow in={heroIn} timeout={1100}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2.5,
                      borderRadius: 2,
                      bgcolor: 'rgba(255,255,255,0.12)',
                      backdropFilter: 'blur(16px)',
                      border: '1px solid rgba(255,255,255,0.25)',
                      color: 'common.white',
                      animation: `${floatY} 5s ease-in-out infinite`,
                    }}
                  >
                    <Typography variant="overline" sx={{ opacity: 0.85, letterSpacing: 1.2 }}>
                      Pipeline
                    </Typography>
                    <Stack spacing={1.25} sx={{ mt: 1 }}>
                      {PIPELINE.map((p, i) => (
                        <Box
                          key={p.n}
                          sx={{
                            display: 'flex',
                            gap: 1.5,
                            alignItems: 'center',
                            py: 0.75,
                            px: 1.25,
                            borderRadius: 1,
                            bgcolor:
                              i === 3
                                ? 'rgba(255,255,255,0.18)'
                                : 'rgba(255,255,255,0.06)',
                          }}
                        >
                          <Box
                            sx={{
                              width: 28,
                              height: 28,
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: 'rgba(255,255,255,0.2)',
                              fontWeight: 700,
                              fontSize: 13,
                            }}
                          >
                            {p.n}
                          </Box>
                          <Box>
                            <Typography variant="body2" fontWeight={700}>
                              {p.name}
                            </Typography>
                            <Typography variant="caption" sx={{ opacity: 0.85 }}>
                              {p.desc}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Stack>
                  </Paper>
                </Grow>
              </Grid>
            )}
          </Grid>
        </Container>

        {/* bottom fade into page */}
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            height: 64,
            background: `linear-gradient(to top, ${theme.palette.background.default}, transparent)`,
            pointerEvents: 'none',
          }}
        />
      </Box>

      {/* —— FEATURES —— */}
      <Box id="features" component="section" sx={{ py: { xs: 6, md: 8 } }}>
        <Container maxWidth="lg">
          <Reveal variant="flip" durationMs={900}>
            <Box sx={{ textAlign: 'center', mb: 5, maxWidth: 560, mx: 'auto' }}>
              <Chip label="Capabilities" size="small" color="primary" variant="outlined" sx={{ mb: 1.5 }} />
              <Typography variant="h4" fontWeight={700} gutterBottom>
                Marketing-grade clarity. Lab-grade math.
              </Typography>
              <Typography variant="body1" color="text.secondary">
                FlahaFAST is a decision support system — not a generic calculator. Every stage
                feeds one Result[] you can defend.
              </Typography>
            </Box>
          </Reveal>

          <Grid container spacing={3}>
            {FEATURES.map((f, i) => (
              <Grid key={f.title} size={{ xs: 12, sm: 6, md: 3 }}>
                <Reveal
                  variant={i % 2 === 0 ? 'up' : 'scale'}
                  delayMs={120 + i * 110}
                  durationMs={850}
                >
                  <Card
                    variant="outlined"
                    sx={{
                      height: '100%',
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s',
                      '&:hover': {
                        transform: 'translateY(-6px)',
                        boxShadow: '0 16px 40px rgba(5, 124, 105, 0.12)',
                        borderColor: 'primary.main',
                      },
                    }}
                  >
                    <CardContent sx={{ p: 2.5 }}>
                      <Box
                        sx={{
                          width: 52,
                          height: 52,
                          borderRadius: 1.5,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: 'rgba(5, 124, 105, 0.08)',
                          color: 'primary.main',
                          mb: 2,
                        }}
                      >
                        {f.icon}
                      </Box>
                      <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                        {f.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" lineHeight={1.6}>
                        {f.body}
                      </Typography>
                    </CardContent>
                  </Card>
                </Reveal>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* —— WORKFLOW —— */}
      <Box
        id="workflow"
        component="section"
        sx={{ py: { xs: 6, md: 8 }, bgcolor: 'background.paper' }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid size={{ xs: 12, md: 5 }}>
              <Reveal variant="left" durationMs={900}>
                <Chip label="Workflow" size="small" color="primary" variant="outlined" sx={{ mb: 1.5 }} />
                <Typography variant="h4" fontWeight={700} gutterBottom>
                  One disciplined path
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph>
                  Water → Crop → Salts → Recipe → Results. No nested calculators, no mystery
                  re-solves. Parameters, output, and verification stay transparent.
                </Typography>
                <Button
                  variant="contained"
                  endIcon={<ArrowForward />}
                  onClick={() => navigate('/login')}
                  sx={{ mt: 1 }}
                >
                  Open workspace
                </Button>
              </Reveal>
            </Grid>
            <Grid size={{ xs: 12, md: 7 }}>
              <Reveal variant="right" delayMs={140} durationMs={950}>
                <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        sm: 'repeat(5, 1fr)',
                      },
                    }}
                  >
                    {PIPELINE.map((p, i) => (
                      <Reveal key={p.n} variant="up" delayMs={200 + i * 90} durationMs={700}>
                        <Box
                          sx={{
                            p: 2,
                            borderRight: {
                              xs: 0,
                              sm: i < 4 ? 1 : 0,
                            },
                            borderBottom: {
                              xs: i < 4 ? 1 : 0,
                              sm: 0,
                            },
                            borderColor: 'divider',
                            textAlign: 'center',
                            transition: 'background 0.25s',
                            '&:hover': { bgcolor: 'rgba(5, 124, 105, 0.04)' },
                          }}
                        >
                          <Typography
                            variant="h5"
                            fontWeight={800}
                            color="primary.main"
                            sx={{ mb: 0.5 }}
                          >
                            {p.n}
                          </Typography>
                          <Typography variant="subtitle2" fontWeight={700}>
                            {p.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {p.desc}
                          </Typography>
                        </Box>
                      </Reveal>
                    ))}
                  </Box>
                </Paper>
              </Reveal>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* —— SOCIAL PROOF STRIP —— */}
      <Box
        sx={{
          py: 3,
          background: 'linear-gradient(90deg, #057c69 0%, #0b7d9d 50%, #15bfd5 100%)',
          backgroundSize: '200% 100%',
          animation: `${shimmer} 10s ease infinite`,
          color: 'common.white',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="lg">
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            justifyContent="space-around"
            textAlign="center"
          >
            {[
              { k: 'Matrix DSS', v: 'Stoichiometric engine' },
              { k: 'Traceable', v: 'Verify + Result[] report' },
              { k: 'Operations', v: 'A/B stock · dilution · PDF' },
            ].map((s, i) => (
              <Reveal key={s.k} variant="scale" delayMs={i * 120} durationMs={750}>
                <Box>
                  <Typography variant="subtitle1" fontWeight={800}>
                    {s.k}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    {s.v}
                  </Typography>
                </Box>
              </Reveal>
            ))}
          </Stack>
        </Container>
      </Box>

      {/* —— PRICING —— */}
      <Box
        id="pricing"
        component="section"
        sx={{
          py: { xs: 8, md: 10 },
          background: `
            radial-gradient(ellipse 70% 50% at 50% 0%, rgba(5, 124, 105, 0.07) 0%, transparent 55%),
            ${theme.palette.background.default}
          `,
        }}
      >
        <Container maxWidth="lg">
          <Reveal variant="flip" durationMs={950}>
            <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 7 }, maxWidth: 520, mx: 'auto' }}>
              <Chip
                label="Pricing"
                size="small"
                color="primary"
                variant="outlined"
                sx={{ mb: 2, fontWeight: 600 }}
              />
              <Typography
                variant="h3"
                fontWeight={800}
                gutterBottom
                sx={{ fontSize: { xs: '1.85rem', md: '2.35rem' }, letterSpacing: '-0.02em' }}
              >
                Choose your plan
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                Transparent tiers for evaluation, production growing, and multi-site teams.
                Upgrade only when you need more capacity and export.
              </Typography>
            </Box>
          </Reveal>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1.08fr 1fr' },
              gap: { xs: 3, md: 3.5 },
              alignItems: 'stretch',
              maxWidth: 1080,
              mx: 'auto',
              pt: 1.5,
            }}
          >
            {PLANS.map((plan, i) => (
              <Reveal
                key={plan.name}
                variant={i === 0 ? 'left' : i === 2 ? 'right' : 'scale'}
                delayMs={100 + i * 140}
                durationMs={900}
              >
                <Box
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    // Lift featured card on desktop without squashing neighbors
                    mt: { md: plan.highlight ? 0 : 2.5 },
                    mb: { md: plan.highlight ? 0 : 2.5 },
                  }}
                >
                  {plan.highlight && (
                    <Chip
                      label="Recommended"
                      size="small"
                      color="primary"
                      sx={{
                        position: 'absolute',
                        top: -14,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        fontWeight: 700,
                        zIndex: 2,
                        boxShadow: '0 4px 14px rgba(5, 124, 105, 0.35)',
                      }}
                    />
                  )}

                  <Paper
                    elevation={0}
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      borderRadius: 3,
                      overflow: 'hidden',
                      border: '1px solid',
                      borderColor: plan.highlight ? 'primary.main' : 'divider',
                      boxShadow: plan.highlight
                        ? '0 24px 56px rgba(5, 124, 105, 0.16)'
                        : '0 8px 28px rgba(0,0,0,0.04)',
                      transition: 'transform 0.35s ease, box-shadow 0.35s ease',
                      bgcolor: 'background.paper',
                      '&:hover': {
                        transform: 'translateY(-6px)',
                        boxShadow: plan.highlight
                          ? '0 28px 64px rgba(5, 124, 105, 0.22)'
                          : '0 16px 40px rgba(0,0,0,0.08)',
                      },
                    }}
                  >
                    {/* Header band */}
                    <Box
                      sx={{
                        px: { xs: 3, md: 3.5 },
                        pt: { xs: 3.5, md: 4 },
                        pb: 2.5,
                        background: plan.highlight
                          ? 'linear-gradient(145deg, rgba(5, 124, 105, 0.1) 0%, rgba(21, 191, 213, 0.08) 100%)'
                          : 'transparent',
                        borderBottom: 1,
                        borderColor: 'divider',
                      }}
                    >
                      <Typography
                        variant="overline"
                        sx={{
                          color: plan.highlight ? 'primary.main' : 'text.secondary',
                          fontWeight: 700,
                          letterSpacing: 1.2,
                        }}
                      >
                        {plan.blurb}
                      </Typography>
                      <Typography
                        variant="h4"
                        fontWeight={800}
                        sx={{ mt: 0.5, letterSpacing: '-0.02em' }}
                      >
                        {plan.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                        {plan.audience}
                      </Typography>

                      <Box sx={{ mt: 2.5, display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                        {plan.currency && (
                          <Typography
                            component="span"
                            variant="h5"
                            fontWeight={700}
                            color={plan.highlight ? 'primary.main' : 'text.primary'}
                            sx={{ alignSelf: 'flex-start', mt: 0.75 }}
                          >
                            {plan.currency}
                          </Typography>
                        )}
                        <Typography
                          component="span"
                          fontWeight={800}
                          color={plan.highlight ? 'primary.main' : 'text.primary'}
                          sx={{
                            fontSize: plan.price === 'Custom' ? '2rem' : '3rem',
                            lineHeight: 1,
                            letterSpacing: '-0.03em',
                          }}
                        >
                          {plan.price}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                        {plan.period}
                      </Typography>
                    </Box>

                    {/* Features */}
                    <Box
                      sx={{
                        px: { xs: 3, md: 3.5 },
                        py: 3,
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <Stack spacing={1.75} sx={{ flex: 1, mb: 3.5 }}>
                        {plan.points.map((pt) => (
                          <Stack
                            key={pt}
                            direction="row"
                            spacing={1.25}
                            alignItems="flex-start"
                          >
                            <CheckCircleOutline
                              sx={{
                                fontSize: 20,
                                color: plan.highlight ? 'primary.main' : 'success.main',
                                mt: 0.15,
                                flexShrink: 0,
                              }}
                            />
                            <Typography variant="body2" color="text.primary" sx={{ lineHeight: 1.5 }}>
                              {pt}
                            </Typography>
                          </Stack>
                        ))}
                      </Stack>

                      <Button
                        fullWidth
                        variant={plan.highlight ? 'contained' : 'outlined'}
                        size="large"
                        endIcon={plan.highlight ? <ArrowForward /> : undefined}
                        onClick={() => {
                          if (plan.action === 'enterprise') contactEnterprise();
                          else if (plan.action === 'pro') goRegister('pro');
                          else goRegister('free');
                        }}
                        sx={{
                          py: 1.4,
                          fontWeight: 700,
                          borderRadius: 2,
                          ...(plan.highlight && {
                            boxShadow: '0 8px 24px rgba(5, 124, 105, 0.3)',
                          }),
                        }}
                      >
                        {plan.cta}
                      </Button>
                    </Box>
                  </Paper>
                </Box>
              </Reveal>
            ))}
          </Box>

          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            textAlign="center"
            sx={{ mt: 4 }}
          >
            Prices in USD. Cancel anytime on self-serve plans. Enterprise terms by agreement.
          </Typography>
        </Container>
      </Box>

      {/* —— CTA BAND —— */}
      <Box
        sx={{
          py: { xs: 5, md: 6 },
          bgcolor: 'background.paper',
          borderTop: 1,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Container maxWidth="md">
          <Reveal variant="flip" durationMs={900}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={3}
              alignItems="center"
              justifyContent="space-between"
            >
              <Box>
                <Typography variant="h5" fontWeight={700} gutterBottom>
                  Ready to formulate with confidence?
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Sign in to the workspace and run Water → Results in one session.
                </Typography>
              </Box>
              <Stack direction="row" spacing={1.5} flexShrink={0}>
                <Button variant="contained" size="large" onClick={() => navigate('/login')}>
                  Sign in
                </Button>
                <Button variant="outlined" size="large" component={RouterLink} to="/support">
                  Docs
                </Button>
              </Stack>
            </Stack>
          </Reveal>
        </Container>
      </Box>

      {/* —— COMPANY —— */}
      <Box component="section" sx={{ py: 5 }}>
        <Container maxWidth="md">
          <Reveal variant="scale" durationMs={850}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={3}
              alignItems="center"
              justifyContent="center"
              textAlign={{ xs: 'center', sm: 'left' }}
            >
              <Logo type="flaha-main" size="medium" />
              <Box>
                <Typography variant="subtitle1" fontWeight={700}>
                  Flaha Agri Tech
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Precision agriculture software. FlahaFAST is the nutrient formulation DSS
                  for professional hydroponics.
                </Typography>
              </Box>
            </Stack>
          </Reveal>
        </Container>
      </Box>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          borderTop: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          py: 2,
        }}
      >
        <Container maxWidth="lg">
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
          >
            <Typography variant="caption" color="text.secondary">
              © {year} Flaha Agri Tech · FlahaFAST DSS
            </Typography>
            <Stack
              direction="row"
              spacing={2}
              sx={{
                '& a': {
                  typography: 'caption',
                  color: 'text.secondary',
                  textDecoration: 'none',
                  '&:hover': { color: 'primary.main' },
                },
              }}
            >
              <Link href="#features">Features</Link>
              <Link href="#pricing">Pricing</Link>
              <Link component={RouterLink} to="/support">
                Support
              </Link>
              <Link component={RouterLink} to="/login">
                Sign in
              </Link>
            </Stack>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
};

export default LandingPage;
